import React, { useState, useRef } from "react";
import { BaseAppProps } from "../../../types/webos";

// Import images
import dogImage from "url:./images/dog.svg";
import cowImage from "url:./images/cow.svg";
import duckImage from "url:./images/duck.svg";
import pigImage from "url:./images/pig.svg";
import horseImage from "url:./images/horse.svg";
import sheepImage from "url:./images/sheep.svg";
import chickenImage from "url:./images/chicken.svg";
import goatImage from "url:./images/goat.svg";
import donkeyImage from "url:./images/donkey.svg";

// Import sounds as URLs
import dogSoundUrl from "url:./sounds/dog.mp3";
import bullSoundUrl from "url:./sounds/bull.mp3";
import duckSoundUrl from "url:./sounds/duck.mp3";
import pigSoundUrl from "url:./sounds/pig.mp3";
import horseSoundUrl from "url:./sounds/horse.mp3";
import sheepSoundUrl from "url:./sounds/sheep.mp3";
import roosterSoundUrl from "url:./sounds/rooster.mp3";
import goatSoundUrl from "url:./sounds/goat.mp3";
import donkeySoundUrl from "url:./sounds/donkey.mp3";

// Animal data with image and sound mappings
const animals = [
  {
    id: "dog",
    name: "Chien",
    image: dogImage,
    sound: dogSoundUrl,
  },
  {
    id: "cow",
    name: "Vache",
    image: cowImage,
    sound: bullSoundUrl,
  },
  {
    id: "duck",
    name: "Canard",
    image: duckImage,
    sound: duckSoundUrl,
  },
  {
    id: "pig",
    name: "Cochon",
    image: pigImage,
    sound: pigSoundUrl,
  },
  {
    id: "horse",
    name: "Cheval",
    image: horseImage,
    sound: horseSoundUrl,
  },
  {
    id: "sheep",
    name: "Mouton",
    image: sheepImage,
    sound: sheepSoundUrl,
  },
  {
    id: "chicken",
    name: "Poule",
    image: chickenImage,
    sound: roosterSoundUrl,
  },
  {
    id: "goat",
    name: "Chevre",
    image: goatImage,
    sound: goatSoundUrl,
  },
  {
    id: "donkey",
    name: "Âne",
    image: donkeyImage,
    sound: donkeySoundUrl,
  },
];

interface AnimalCardProps {
  animal: (typeof animals)[0];
  onPlay: (soundPath: string) => void;
}

function AnimalCard({ animal, onPlay }: AnimalCardProps) {
  const [isShaking, setIsShaking] = useState(false);

  const handleClick = () => {
    if (navigator.vibrate) {
      navigator.vibrate(1001);
    }

    // Start shake animation
    setIsShaking(true);
    setTimeout(() => setIsShaking(false), 600);

    // Play sound
    onPlay(animal.sound);
  };

  return (
    <div
      className={`
        cursor-pointer
        transform transition-all duration-200 hover:scale-105
        ${isShaking ? "animate-bounce" : ""}
      `}
      onClick={handleClick}
      style={{
        animation: isShaking ? "shake 0.6s ease-in-out" : undefined,
      }}
    >
      <div className="flex flex-col items-center space-y-4">
        <div className="w-32 h-32 md:w-40 md:h-40 flex items-center justify-center bg-gray-50 rounded-2xl">
          <img
            src={animal.image}
            alt={animal.name}
            className="w-24 h-24 md:w-32 md:h-32 object-contain"
            draggable={false}
          />
        </div>
        <h3 className="text-2xl md:text-3xl font-bold text-gray-800 text-center">
          {animal.name}
        </h3>
      </div>
    </div>
  );
}

export function App({ onClose }: BaseAppProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const playSound = (soundPath: string) => {
    try {
      // Stop any currently playing sound
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }

      // Create new audio instance
      const audio = new Audio(soundPath);
      audioRef.current = audio;

      // Play the sound
      audio.play().catch((error) => {
        console.warn("Could not play sound:", error);
      });
    } catch (error) {
      console.warn("Error playing sound:", error);
    }
  };

  return (
    <div className="h-full bg-gradient-to-br from-blue-200 via-purple-200 to-pink-200 p-6 overflow-auto">
      {/* Animal Grid */}
      <div className="grid grid-cols-2 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {animals.map((animal) => (
          <AnimalCard key={animal.id} animal={animal} onPlay={playSound} />
        ))}
      </div>

      {/* Custom CSS for shake animation */}
      <style>{`
        @keyframes shake {
          0%,
          100% {
            transform: translateX(0);
          }
          10%,
          30%,
          50%,
          70%,
          90% {
            transform: translateX(-5px);
          }
          20%,
          40%,
          60%,
          80% {
            transform: translateX(5px);
          }
        }
      `}</style>
    </div>
  );
}
